
import { defineComponent, ref, watch } from 'vue';
import { FormModalShell } from '@/components/shared/modal';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import {
  BLOCKED_KHOA_PREFIXES,
  defaultGiaTriForLoai,
  filterValidTuyChon,
  LOAI_GIA_TRI_OPTIONS,
} from '@/core/helpers/cai-dat';
import { swalErrNotification } from '@/core/helpers/utils';
import {
  CaiDatCreatePayload,
  CaiDatService,
} from '@/core/services/CaiDat.service';
import {
  CaiDatLoaiGiaTri,
  ICaiDatTuyChon,
} from '@/core/interfaces/ApiResponses';
import CaiDatGiaTriControl from './CaiDatGiaTriControl.vue';
import Swal from 'sweetalert2';

const emptyForm = () => ({
  khoa: '',
  loai_gia_tri: 'text' as CaiDatLoaiGiaTri,
  gia_tri: '',
  ghi_chu: '',
  tuy_chon: [{ value: '', label: '' }] as ICaiDatTuyChon[],
});

export default defineComponent({
  name: 'cai-dat-create-modal',

  components: {
    FormModalShell,
    ButtonCustom,
    CaiDatGiaTriControl,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'success'],

  setup(props, { emit }) {
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const formRef = ref<null | HTMLFormElement>(null);
    const saving = ref(false);
    const form = ref(emptyForm());
    const loaiGiaTriOptions = LOAI_GIA_TRI_OPTIONS;

    const rules = {
      khoa: [
        { required: true, message: 'Vui lòng nhập khóa', trigger: 'blur' },
        {
          validator: (
            _rule: unknown,
            value: string,
            callback: (e?: Error) => void
          ) => {
            const khoa = (value || '').trim().toUpperCase();
            if (BLOCKED_KHOA_PREFIXES.some((prefix) => khoa.startsWith(prefix))) {
              callback(
                new Error(
                  'Không được tạo khóa hệ thống Kho (KHO_CHECKPOINT_DATE_* / KHO_INITIAL_BALANCE_DATE_*)'
                )
              );
              return;
            }
            callback();
          },
          trigger: 'blur',
        },
      ],
      loai_gia_tri: [
        {
          required: true,
          message: 'Vui lòng chọn kiểu giá trị',
          trigger: 'change',
        },
      ],
      tuy_chon: [
        {
          validator: (
            _rule: unknown,
            _value: unknown,
            callback: (e?: Error) => void
          ) => {
            if (form.value.loai_gia_tri !== 'dropdown') {
              callback();
              return;
            }

            if (!filterValidTuyChon(form.value.tuy_chon).length) {
              callback(
                new Error('Dropdown cần ít nhất một tuỳ chọn (value + label)')
              );
              return;
            }

            callback();
          },
          trigger: 'change',
        },
      ],
    };

    const resetForm = () => {
      form.value = emptyForm();
      (formRef.value as any)?.clearValidate?.();
    };

    watch(
      () => props.isOpen,
      (open) => {
        if (open) {
          resetForm();
        }
      }
    );

    const onClose = () => {
      emit('close');
    };

    const onLoaiChange = (loai: CaiDatLoaiGiaTri) => {
      form.value.gia_tri = defaultGiaTriForLoai(loai);
      if (loai === 'dropdown' && !form.value.tuy_chon.length) {
        form.value.tuy_chon = [{ value: '', label: '' }];
      }
    };

    const addTuyChon = () => {
      form.value.tuy_chon.push({ value: '', label: '' });
    };

    const removeTuyChon = (index: number) => {
      if (form.value.tuy_chon.length <= 1) return;
      form.value.tuy_chon.splice(index, 1);
      const values = form.value.tuy_chon.map((opt) => opt.value);
      if (!values.includes(form.value.gia_tri)) {
        form.value.gia_tri = '';
      }
    };

    const submit = () => {
      if (!formRef.value) return;

      (formRef.value as any).validate(async (valid: boolean) => {
        if (!valid) return;

        saving.value = true;
        try {
          const payload: CaiDatCreatePayload = {
            khoa: form.value.khoa.trim(),
            loai_gia_tri: form.value.loai_gia_tri,
          };

          const giaTri = String(form.value.gia_tri ?? '').trim();
          if (giaTri !== '') {
            payload.gia_tri = giaTri;
          }
          if (form.value.ghi_chu.trim() !== '') {
            payload.ghi_chu = form.value.ghi_chu.trim();
          }
          if (form.value.loai_gia_tri === 'dropdown') {
            payload.tuy_chon = filterValidTuyChon(form.value.tuy_chon);
          }

          const {
            data: { data },
          } = await CaiDatService.create(payload);

          await Swal.fire({
            icon: 'success',
            title: 'Thành công',
            text: `Đã tạo khóa và seed cho ${data.seeded_count} đơn vị`,
            timer: 2000,
            showConfirmButton: false,
          });

          emit('success');
          emit('close');
        } catch (error) {
          await swalErrNotification(error, 'Có lỗi khi tạo khóa cài đặt');
        } finally {
          saving.value = false;
        }
      });
    };

    return {
      ButtonsType,
      ButtonTypeColors,
      formRef,
      form,
      rules,
      saving,
      loaiGiaTriOptions,
      onClose,
      onLoaiChange,
      addTuyChon,
      removeTuyChon,
      submit,
    };
  },
});
